<template>
  <v-card class="d-flex flex-column" style="height: 100%;">
    <v-card-title class="pb-2">
      <v-icon class="mr-1">mdi-forum-outline</v-icon>
      Verlauf
      <v-chip v-if="comments.length" x-small label class="ml-2">
        {{ comments.length }}
      </v-chip>
    </v-card-title>

    <v-divider />

    <div class="pa-3">
      <v-textarea
        v-model="draft"
        placeholder="Nachricht schreiben … (Enter zum Senden, Umschalt+Enter für neue Zeile)"
        outlined
        dense
        hide-details
        rows="1"
        auto-grow
        :disabled="sending"
        @keydown.enter.exact.prevent="send"
      >
        <template v-slot:append>
          <v-btn
            icon
            color="primary"
            :loading="sending"
            :disabled="!draft.trim()"
            @click="send"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </template>
      </v-textarea>
    </div>

    <v-divider />

    <div ref="scroll" class="chat-scroll px-4 py-3">
      <div v-if="!comments.length" class="text-caption grey--text text-center py-6">
        Noch keine Nachrichten. Schreib die erste.
      </div>

      <template v-for="(c, i) in comments">
        <!-- System-/Aktivitätseintrag -->
        <div v-if="c.kind === 'system'" :key="c.id || 'sys-' + i" class="chat-system">
          <v-icon x-small class="mr-1">{{ c.icon || 'mdi-information-outline' }}</v-icon>
          <span>{{ c.text }}</span>
          <span class="chat-system-time">· {{ formatTime(c.created_at) }}</span>
        </div>

        <!-- Nachricht -->
        <div
          v-else
          :key="c.id || 'msg-' + i"
          class="chat-row"
          :class="{ 'chat-row-own': isOwn(c) }"
        >
          <div class="chat-bubble" :class="{ 'chat-bubble-own': isOwn(c) }">
            <div class="chat-meta">
              <span class="chat-author">{{ authorName(c) }}</span>
              <span class="chat-time" :title="formatDateTime(c.created_at)">
                {{ formatTime(c.created_at) }}
              </span>
            </div>
            <div class="chat-text">{{ c.text }}</div>
          </div>
        </div>
      </template>
    </div>
  </v-card>
</template>

<script>
export default {
  name: "TicketChat",
  props: {
    ticket: { type: Object, required: true },
  },
  data: () => ({
    draft: "",
    sending: false,
  }),
  computed: {
    comments() {
      const list = Array.isArray(this.ticket.comments) ? this.ticket.comments : []
      // Neueste oben ("auf den Stapel drauflegen")
      return list.slice().reverse()
    },
    currentUserId() {
      return this.$store.state.user.id
    },
    users() {
      return this.$store.state.ticketsystem.users
    },
  },
  watch: {
    "comments.length"() {
      this.scrollToTop()
    },
    "ticket.id"() {
      this.draft = ""
      this.scrollToTop()
    },
  },
  mounted() {
    this.scrollToTop()
  },
  methods: {
    isOwn(c) {
      return c.author_id != null && c.author_id === this.currentUserId
    },
    authorName(c) {
      if (c.author_id == null) return "System"
      const u = this.users.find(x => x.id === c.author_id)
      return u ? u.displayName : "Unbekannt"
    },
    formatTime(s) {
      if (!s) return ""
      const d = new Date(s)
      const today = new Date()
      const sameDay = d.toDateString() === today.toDateString()
      if (sameDay) {
        return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
      }
      return d.toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })
    },
    formatDateTime(s) {
      if (!s) return ""
      return new Date(s).toLocaleString("de-DE")
    },
    scrollToTop() {
      this.$nextTick(() => {
        const el = this.$refs.scroll
        if (el) el.scrollTop = 0
      })
    },
    async send() {
      const text = this.draft.trim()
      if (!text || this.sending) return
      this.sending = true
      try {
        await this.$store.dispatch("update", {
          payload_type: "POST",
          context: `/tickets/${this.ticket.id}/comments`,
          payload: { text },
        })
        this.draft = ""
        this.scrollToTop()
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style scoped>
.chat-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 240px;
  max-height: 520px;
  background: rgba(0, 0, 0, 0.015);
}
.chat-row {
  display: flex;
  margin-bottom: 8px;
}
.chat-row-own {
  justify-content: flex-end;
}
.chat-bubble {
  max-width: 78%;
  padding: 6px 10px;
  border-radius: 12px;
  border-top-left-radius: 4px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.chat-bubble-own {
  background: rgba(123, 0, 0, 0.07);
  border-color: rgba(123, 0, 0, 0.25);
  border-top-left-radius: 12px;
  border-top-right-radius: 4px;
}
.chat-meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 2px;
}
.chat-author {
  font-size: 0.75rem;
  font-weight: 600;
  color: #7b0000;
}
.chat-time {
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.5);
}
.chat-text {
  font-size: 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
}
.chat-system {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.72rem;
  color: rgba(0, 0, 0, 0.55);
  margin: 6px 0;
}
.chat-system-time {
  font-size: 0.68rem;
  color: rgba(0, 0, 0, 0.4);
}
</style>
